import React, { Dispatch } from 'react';
import { RootAction } from 'store/state';
import {
  AvatarSection,
  BottomLinksSectionWrapper,
  LinksSectionWrapper,
  MobileMenuBody,
  MobileMenuContentWrapper,
  MobileMenuHeader,
  MobileMenuWrapper,
  MoreMenuLinksWithIconsSection,
  MyProfileText,
  NewLabelV2,
  SavedListingsCount,
  ProfileShortcutItem,
  UnloggedUserAvatarIconWrapper,
  UnloggedUserAvatarSection,
  UploadListingButton,
  UserInfo,
  UserInfoWithAvatarWrapper,
  UserSection,
  LinksDrawerInner,
  ProfileShortcutWrapper,
} from './styled';
import { connect } from 'react-redux';
import { State } from 'store/state';
import { OverlayWrapper } from 'components/overlay-wrapper';
import { useLocale } from 'locale';
import { MenuLink } from './MenuLink';
import Logo from 'components/navigation/Logo';
import CloseIcon from 'assets/svg/dropdown/close.svg';
import UnloggedUserAvatarIcon from 'assets/svg/navigation/unlogged-user-avatar.svg';
import ChevronIcon from 'assets/svg/navigation/chevron-left.svg';
import BriefcaseIcon from 'assets/svg/navigation/briefcase.svg';
import ExclamationMarkIcon from 'assets/svg/navigation/exclamation-mark.svg';
import { Avatar, BetaWrapper } from '../header/styled';
import { typographyFontSizeCfg } from 'ds/components/typography/types';
import { IUserProfileFields, userProfileFieldsSelector } from 'components/navigation/profileFields';
import { isConnectedUserSelector, isPrivateUserSelector, isVisitorUserSelector } from 'store/state/domainData/selectors';
import { DecodedRouteParams, NavigateToFn, Route } from 'config/routes';
import { Link } from 'components/link';
import { H3, Text } from 'ds/components/typography';
import { setAuthModal, setUserMobileProfileOpen, setUserMobileProfileReturnable } from 'store/state/app/actions';
import { actionToIconMobile, UserActionName, mobileMoreMenuActions, UserActionEventData } from '../userActions';
import { navigateTo } from 'store/state/router/actions';
import { NavigationOptions } from 'store/state/router/actionPayloads';
import { getRouteParams, showAgentConsoleBulletinFormSelector } from 'store/state/selectors/router';
import { agentsLinks, informationAndServicesLinks, staticLinksSelector } from './staticLinks';
import { InformationAndServicesLinksSection, LinksSection, LinksSectionLink, SectionToLinks } from 'components/navigation/more-actions-menu/types';
import { LinksDrawer } from 'components/navigation/more-actions-menu/LinksDrawer';
import { AuthenticationModalPayload, AuthenticationModalType } from 'components/authentication/types';
import { AuthModalSource } from 'store/state/app';
import { userFavoritesCounterSelector } from 'store/state/domainData/selectors/favorites';
import { pushNotificationsEnabledSelector } from 'store/state/selectors/pushNotificationsStatus';
import { pushNotificationsFeatureEnabled } from 'utils/pushNotificationsFeatureEnabled';
import { savedSearchesCountSelector } from 'store/state/selectors/savedSearchPage';
import { isMenuDotVisibleSelector } from 'store/state/app/selectors';
import { useSendEvent } from 'analytics/useSendEvent';
import withEvents from 'analytics/withEvents';


interface UserMobileMoreMenuProps {
  userProfile: IUserProfileFields;
  isConnectedUser: boolean;
  isVisitor: boolean;
  isMenuDotVisible: boolean;
  showAgentConsoleBulletinForm: boolean;
  pushEnabled: boolean;
  savedListingsCount: number;
  savedSearchesCount: number;
  routeParams: DecodedRouteParams;
  staticLinks: SectionToLinks;
  onClose: () => void;
  onSetUserMobileProfileOpen: (isOpen: boolean) => void;
  onSetUserMobileProfileReturnable: (isReturnable: boolean) => void;
  onSetAuthModal: typeof setAuthModal;
  navigate: NavigateToFn;
  onClickEvent?: (data: UserActionEventData) => void;
  isPrivateUser?: boolean;
}

const UserMobileMoreMenu: React.FC<UserMobileMoreMenuProps> = ({
  userProfile,
  isConnectedUser,
  isVisitor,
  isMenuDotVisible,
  showAgentConsoleBulletinForm,
  savedListingsCount,
  savedSearchesCount,
  routeParams,
  staticLinks,
  onClose,
  onSetUserMobileProfileOpen,
  onSetUserMobileProfileReturnable,
  onSetAuthModal,
  navigate,
  pushEnabled,
  onClickEvent,
  isPrivateUser,
}) => {
  const { t } = useLocale();
  const sendEvent = useSendEvent();

  const isLoggedInUser = isConnectedUser && userProfile && userProfile.initials;
  const bottomSection = staticLinks[LinksSection.Localize];

  const handleAvatarSectionClick = () => {
    onSetUserMobileProfileOpen(true);
    onSetUserMobileProfileReturnable(true);
  };

  const handleSavedListingsClick = () => {
    navigate(Route.Shortlist, routeParams);
    sendEvent('homepage_sidebar_menu_saved_search', 'homepage', {
      event: {
        page_type: 'favorites_page',
        tab_type: 'favorites_listing',
      },
    });
    onClose();
  };

  const handleSavedSearchesClick = () => {
    navigate(Route.SavedSearchesPage, routeParams);
    sendEvent('homepage_sidebar_menu_saved_search', 'homepage', {
      event: {
        page_type: 'favorites_page',
        tab_type: 'saved_search',
      },
    });
    onClose();
  };

  const handleClickEvent = (data: UserActionEventData) => {
    onClickEvent(data);
    onClose();
  };

  return (
    <OverlayWrapper>
      <MobileMenuWrapper style={{ minHeight: window ? window.innerHeight : 'auto' }}>
        <MobileMenuContentWrapper>
          <MobileMenuHeader whiteBackground>
            <CloseIcon onClick={onClose} />
            <Logo />
          </MobileMenuHeader>
          <MobileMenuBody>
            <UserSection>
              {isLoggedInUser ? (
                <AvatarSection onClick={handleAvatarSectionClick}>
                  <UserInfoWithAvatarWrapper>
                    <Avatar
                      userInitials={userProfile.initials}
                      imageURL={userProfile.avatar}
                      style={{ height: 56, width: 56, fontSize: typographyFontSizeCfg.h2.mobile }}
                    />
                    <UserInfo data-auto="mobile-user-info">
                      <H3 weight="bold">
                        {userProfile.firstName} {userProfile.lastName}
                      </H3>
                      <MyProfileText>{t('navigation.moreActionsMenu.myProfile')}</MyProfileText>
                    </UserInfo>
                  </UserInfoWithAvatarWrapper>
                  <ChevronIcon />
                </AvatarSection>
              ) : (
                <UnloggedUserAvatarSection
                  onClick={() => onSetAuthModal({ type: AuthenticationModalType.SignIn }, { isUserInitiated: true })}
                >
                  <UnloggedUserAvatarIconWrapper>
                    <UnloggedUserAvatarIcon />
                  </UnloggedUserAvatarIconWrapper>
                  {t('navigation.moreActionsMenu.loginOrRegister')}
                </UnloggedUserAvatarSection>
              )}
              <Link
                routeName={
                  showAgentConsoleBulletinForm
                    ? Route.UploadBulletinForm
                    : Route.UploadBulletin
                }
                routeParams={{
                  source: 'homepage_sidebar_menu',
                }}
                preserveParams
                style={{ textDecoration: 'none' }}
                onClick={onClose}
              >
                <UploadListingButton size="extraLarge">
                  {t('navigation.moreActionsMenu.uploadBulletin')}
                </UploadListingButton>
              </Link>
              {isLoggedInUser && (
                <ProfileShortcutWrapper>
                  <ProfileShortcutItem onClick={handleSavedListingsClick}>
                    <MenuLink isInitialIconColor>
                      {actionToIconMobile[UserActionName.SavedListings]}
                      <Text>{t('navigation.header.actions', { action: UserActionName.SavedListings })}</Text>
                    </MenuLink>
                    <SavedListingsCount>{savedListingsCount}</SavedListingsCount>
                  </ProfileShortcutItem>
                  <ProfileShortcutItem onClick={handleSavedSearchesClick}>
                    <MenuLink isInitialIconColor>
                      {actionToIconMobile[UserActionName.SavedSearches]}
                      <Text>{t('navigation.header.actions', { action: UserActionName.SavedSearches, isProfileMobileMenu: true })}</Text>
                    </MenuLink>
                    <SavedListingsCount isActive={isMenuDotVisible}>{savedSearchesCount}</SavedListingsCount>
                  </ProfileShortcutItem>
                  {pushNotificationsFeatureEnabled() ? (
                    <ProfileShortcutItem>
                      <MenuLink
                        routeName={Route.PushSettings}
                        hasWarning={!pushEnabled}
                        fullWidthAnchor
                        isInitialIconColor
                        onClick={onClose}
                      >
                        {actionToIconMobile.pushSettings}
                        {t('navigation.header.actions', { action:  UserActionName.PushSettings, isProfileMobileMenu: true })}
                      </MenuLink>
                    </ProfileShortcutItem>
                  ) : null}
                </ProfileShortcutWrapper>
              )}
            </UserSection>
            <MoreMenuLinksWithIconsSection>
              <LinksSectionWrapper>
                {mobileMoreMenuActions.map((action) => (isVisitor && action.route === Route.InvestorsHomePage) || (!isPrivateUser && action.name === UserActionName.MeterReport) ? null : (
                  <MenuLink
                    key={action.name}
                    routeName={action.route}
                    routeParams={action.params}
                    href={action.customRoute || undefined}
                    isInitialIconColor
                    fullWidthAnchor
                    onClick={action.eventData ? () => handleClickEvent(action.eventData) : onClose}
                  >
                    {actionToIconMobile[action.name]}
                    {t('navigation.header.actions', {
                      action: action.name,
                      isAgent: userProfile.isAgent,
                      isProfileMobileMenu: true,
                    })}
                    {action.isBeta ? <BetaWrapper>{t('beta')}</BetaWrapper> : null}
                    {action.isNew && (
                      <NewLabelV2 weight="bold" small>
                        {t('navigation.header.new')}
                      </NewLabelV2>
                    )}
                  </MenuLink>
                ))}
                <LinksDrawer links={agentsLinks} onClick={onClose}>
                  <LinksDrawerInner data-auto="more-agents">
                    <BriefcaseIcon />
                    {t('navigation.moreActionsMenu.agents')}
                  </LinksDrawerInner>
                </LinksDrawer>
                <LinksDrawer
                  links={informationAndServicesLinks}
                  sections={[
                    InformationAndServicesLinksSection.Main,
                    InformationAndServicesLinksSection.GuidesAndTools,
                    InformationAndServicesLinksSection.ManagementSystems,
                  ]}
                  onClick={onClose}
                >
                  <LinksDrawerInner data-auto="more-info">
                    <ExclamationMarkIcon />
                    {t('navigation.moreActionsMenu.informationAndServices')}
                  </LinksDrawerInner>
                </LinksDrawer>
              </LinksSectionWrapper>
            </MoreMenuLinksWithIconsSection>
            <BottomLinksSectionWrapper>
              {bottomSection.map((link: LinksSectionLink) => (
                <MenuLink
                  key={link.label}
                  routeName={link.routeName}
                  routeParams={link.routeParams}
                  externalPath={link.externalPath}
                  externalParams={link.externalParams}
                  href={link.path}
                  onClick={onClose}
                  fullWidthAnchor
                  grey2Color
                >
                  {link.label}
                </MenuLink>
              ))}
            </BottomLinksSectionWrapper>
          </MobileMenuBody>
        </MobileMenuContentWrapper>
      </MobileMenuWrapper>
    </OverlayWrapper>
  );
};

const mapStateToProps = (state: State) => ({
  userProfile: userProfileFieldsSelector(state),
  isConnectedUser: isConnectedUserSelector(state),
  isVisitor: isVisitorUserSelector(state),
  showAgentConsoleBulletinForm: showAgentConsoleBulletinFormSelector(state),
  savedListingsCount: userFavoritesCounterSelector(state),
  savedSearchesCount: savedSearchesCountSelector(state),
  routeParams: getRouteParams(state),
  staticLinks: staticLinksSelector(state),
  pushEnabled: pushNotificationsEnabledSelector(state),
  isMenuDotVisible: isMenuDotVisibleSelector(state),
  isPrivateUser: isPrivateUserSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => ({
  onSetUserMobileProfileOpen: (isOpen: boolean) => dispatch(setUserMobileProfileOpen(isOpen)),
  onSetUserMobileProfileReturnable: (isReturnable: boolean) => dispatch(setUserMobileProfileReturnable(isReturnable)),
  onSetAuthModal: (payload: AuthenticationModalPayload, meta: { isUserInitiated?: boolean, source?: AuthModalSource }) => dispatch(setAuthModal(payload, meta)),
  navigate: (name: Route, params?: DecodedRouteParams, opts?: NavigationOptions) => dispatch(navigateTo(name, params, opts)),
});

const UserMobileMoreMenuWithEvents = withEvents<UserMobileMoreMenuProps>(sendEvent => ({
  onClickEvent(data: UserActionEventData) {
    sendEvent(data.name, data.category, { event: data.event });
  },
}))(UserMobileMoreMenu);

export default connect(mapStateToProps, mapDispatchToProps)(UserMobileMoreMenuWithEvents);
